// This is the base class for all our openapi repositories. Any repository
// that is automatically generated through an openapi spec use these base
// class.
import type {
    IRepository,
    IFields,
    IRepositoryManager,
    ActionPaths,
    SupportedContentTypes,
    IRepositoryParameters,
    RepositoryAction,
} from "@/wc/registry/lib/service/repository/types.js";
import {
    BaseRepositoryManager,
    BaseRepositoryPropertiesChecker,
    RepositoryDoesNotSupportActionError,
    RepositoryEventTarget,
} from "@/wc/registry/lib/service/repository/base.js";
import { request } from "@/lib/requests";

const ACCEPTED_STATUS_CODE = [200, 201, 204];

export class OpenAPIRepositoryManager
    extends BaseRepositoryManager
    implements IRepositoryManager
{
    async get(props: IRepositoryParameters, fields?: IFields | FormData) {
        const requestUrl = this.buildRequestUrl("get", props);
        const options: RequestInit = {
            method: this.repository.actionPaths.get.method,
        };

        if (fields && Object.keys(fields).length > 0) {
            options.body = this.getRequestBody(fields, "get");
        }

        const response = await request(requestUrl, options, {
            "Content-Type": this.getContentType("get"),
        });
        return response;
    }

    // Haven't added a generic way of handling request errors yet etc,
    // should also be a part of ticket #18.
    async all(props: IRepositoryParameters, fields?: IFields | FormData) {
        const requestUrl = this.buildRequestUrl("all", props);
        const options: RequestInit = {
            method: this.repository.actionPaths.all.method,
        };

        if (fields && Object.keys(fields).length > 0) {
            options.body = this.getRequestBody(fields, "all");
        }

        const response = await request(requestUrl, options, {
            "Content-Type": this.getContentType("all"),
        });
        return response;
    }

    async partialUpdate(
        props: IRepositoryParameters,
        fields?: IFields | FormData
    ) {
        const requestUrl = this.buildRequestUrl("partialUpdate", props);
        const requestOptions = {};
        const contentType = this.getContentType("partialUpdate");

        // Avoid setting the content type ourselves in case of formdata, so that
        // the browser can configure it and set the correct boundaries.
        if (contentType !== "multipart/form-data") {
            requestOptions["Content-Type"] = contentType;
        }

        const response = await request(
            requestUrl,
            {
                method: this.repository.actionPaths.partialUpdate.method,
                body: this.getRequestBody(fields, "partialUpdate"),
            },
            requestOptions
        );

        if (!ACCEPTED_STATUS_CODE.includes(response.status)) {
            return response;
        }

        return this.handleResponse("partialUpdate", response);
    }

    async update(props: IRepositoryParameters, fields?: IFields | FormData) {
        const requestUrl = this.buildRequestUrl("update", props);
        const requestOptions = {};
        const contentType = this.getContentType("update");

        // Avoid setting the content type ourselves in case of formdata, so that
        // the browser can configure it and set the correct boundaries.
        if (contentType !== "multipart/form-data") {
            requestOptions["Content-Type"] = contentType;
        }

        const response = await request(
            requestUrl,
            {
                method: this.repository.actionPaths.update.method,
                body: this.getRequestBody(fields, "update"),
            },
            requestOptions
        );

        if (!ACCEPTED_STATUS_CODE.includes(response.status)) {
            return response;
        }

        return this.handleResponse("update", response);
    }

    async create(props: IRepositoryParameters, fields?: IFields | FormData) {
        const requestUrl = this.buildRequestUrl("create", props);
        const requestOptions = {};
        const contentType = this.getContentType("create");

        // Avoid setting the content type ourselves in case of formdata, so that
        // the browser can configure it and set the correct boundaries.
        if (contentType !== "multipart/form-data") {
            requestOptions["Content-Type"] = contentType;
        }

        const response = await request(
            requestUrl,
            {
                method: this.repository.actionPaths.create.method,
                body: this.getRequestBody(fields, "create"),
            },
            requestOptions
        );

        if (!ACCEPTED_STATUS_CODE.includes(response.status)) {
            return response;
        }

        return this.handleResponse("create", response);
    }

    async delete(props: IRepositoryParameters, fields?: IFields | FormData) {
        const requestUrl = this.buildRequestUrl("delete", props);
        const options: RequestInit = {
            method: this.repository.actionPaths.delete.method,
        };

        if (fields && Object.keys(fields).length > 0) {
            options.body = this.getRequestBody(fields, "delete");
        }

        const response = await request(requestUrl, options, {
            "Content-Type": this.getContentType("delete"),
        });
        this.repository.events.dispatchEvent(new CustomEvent("deleted"));
        this.repository.events.dispatchEvent(
            new CustomEvent("repository:deleted")
        );
        return response;
    }

    info(action: string): boolean {
        if (!this.repository.actionPaths[action]) {
            throw new RepositoryDoesNotSupportActionError(
                `Repository '${this.repository.name}' does not have the '${action}' path configured in its paths.`
            );
        }

        return this.repository.actionPaths[action].info;
    }

    getContentType(action: string): SupportedContentTypes {
        return this.repository.actionPaths[action].requestBodyContentType;
    }

    getRequestBody(fields: IFields | FormData, action: string) {
        // FormData can be passed directly, in which case we don't need to
        // construct a request body ourselves.
        if (fields instanceof FormData) {
            return fields;
        }

        const contentType = this.getContentType(action);
        if (contentType === "application/json") {
            return JSON.stringify(fields);
        } else if (contentType === "application/x-www-form-urlencoded") {
            const formData = new URLSearchParams();
            Object.keys(fields).forEach((key) =>
                formData.append(key, `${fields[key]}`)
            );
            return formData;
        } else if (contentType === "multipart/form-data") {
            const formData = new FormData();
            Object.keys(fields).forEach((key) =>
                formData.append(key, `${fields[key]}`)
            );
            return formData;
        }
    }
}

export default class OpenAPIGeneratedRepository implements IRepository {
    name: string;
    actionPaths: ActionPaths;
    events: RepositoryEventTarget;
    constructor(name: string, actionPaths: ActionPaths) {
        this.name = name;
        this.actionPaths = actionPaths;
        this.events = new RepositoryEventTarget(this.name);
    }
    objects = new OpenAPIRepositoryManager(this);
    properties = new BaseRepositoryPropertiesChecker(this);

    getActionForMethod(method: string): RepositoryAction {
        return Object.keys(this.actionPaths).find(
            (action) => this.actionPaths[action].method === method
        ) as RepositoryAction;
    }
}
